import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Dialog, List, ListItemButton, ListItemText, Stack, TextField, Typography,
} from '@mui/material';
import StyleConstants from '../../../core/styles.constants';
import type { EditorCommand, EditorCommandGroup } from '../models/editor-command';
import { filterByLabel } from '../utils/command-filter';

interface Props {
  readonly open: boolean;
  readonly commands: readonly EditorCommand[];
  readonly onClose: () => void;
}

const GROUP_LABEL_KEYS: Readonly<Record<EditorCommandGroup, string>> = {
  tools: 'floorplan-editor-command-group-tools',
  edit: 'floorplan-editor-command-group-edit',
  arrange: 'floorplan-editor-command-group-arrange',
  view: 'floorplan-editor-command-group-view',
  plan: 'floorplan-editor-command-group-plan',
};

/**
 * Everything the editor can do, by name.
 *
 * The editor has more actions than any rail can hold — twelve tools, six alignments, four
 * snap modes, the view controls and the plan-level actions — and the ones that end up buried
 * are the ones nobody finds. This is the flat list, searchable, and it costs one keystroke
 * to reach.
 *
 * Runs against translated labels, so a Dutch admin types Dutch. The keys are matched
 * unordered, so "left align" finds the same command as "align left".
 */
const CommandPalette = ({ open, commands, onClose }: Props): React.JSX.Element => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const listRef = useRef<HTMLUListElement | null>(null);

  const labelled = useMemo(
    () => commands.map((command) => ({ command, label: String(t(command.labelKey)) })),
    [commands, t],
  );

  const matches = useMemo(
    () => filterByLabel(labelled, query, (entry) => entry.label),
    [labelled, query],
  );

  // Back to the top whenever the list changes under the cursor, so Enter never runs a
  // command that scrolled out from under the highlight as the query narrowed.
  useEffect(() => setHighlighted(0), [query, open]);

  const run = (command: EditorCommand): void => {
    if (command.disabled) {
      return;
    }

    onClose();
    command.run();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onExited: (): void => setQuery('') }}
    >
      <Stack sx={{ p: 2 }} spacing={1}>
        <TextField
          autoFocus
          size="small"
          value={query}
          onChange={(event): void => setQuery(event.target.value)}
          placeholder={String(t('floorplan-editor-command-placeholder'))}
          label={String(t('floorplan-editor-commands'))}
          autoComplete="off"
          inputProps={{ 'aria-label': String(t('floorplan-editor-commands')) }}
          onKeyDown={(event): void => {
            // The whole list is driven from the field, because the field keeps focus: moving
            // focus into the list on every arrow press would mean typing another letter
            // takes the admin back out of it again.
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setHighlighted((current) => Math.min(current + 1, matches.length - 1));
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              setHighlighted((current) => Math.max(current - 1, 0));
            } else if (event.key === 'Enter' && matches[highlighted]) {
              event.preventDefault();
              run(matches[highlighted].command);
            }
          }}
        />

        {matches.length === 0 ? (
          <Typography variant="body2" color={StyleConstants.GREY_COLOR} sx={{ p: 2 }}>
            {t('floorplan-editor-command-none')}
          </Typography>
        ) : (
          <List dense ref={listRef} sx={{ maxHeight: 360, overflowY: 'auto' }}>
            {matches.map(({ command, label }, index) => (
              <ListItemButton
                key={command.id}
                selected={index === highlighted}
                disabled={command.disabled}
                onClick={(): void => run(command)}
                onMouseEnter={(): void => setHighlighted(index)}
                sx={{ borderRadius: 1 }}
              >
                <ListItemText
                  primaryTypographyProps={{ variant: 'body2' }}
                  primary={label}
                  secondaryTypographyProps={{ variant: 'caption' }}
                  secondary={String(t(GROUP_LABEL_KEYS[command.group]))}
                />
                {command.shortcut && (
                  <Box
                    component="kbd"
                    sx={{
                      border: `1px solid ${StyleConstants.LIGHT_GREY_COLOR}`,
                      borderRadius: 1,
                      px: 1,
                      py: 0.25,
                      fontSize: 12,
                      whiteSpace: 'nowrap',
                      color: StyleConstants.GREY_COLOR,
                    }}
                  >
                    {command.shortcut}
                  </Box>
                )}
              </ListItemButton>
            ))}
          </List>
        )}
      </Stack>
    </Dialog>
  );
};

export default CommandPalette;
